import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MemberDashboardService } from '../member-dashboard.service';

interface Quest {
  memberId: string;
  questName: string;
  questShortDescription: string;
  questThumbnail: string;
  introduction: {
    description: string;
    thumbnail: string;
  };
  startDateTime: string;
  completedDateTime: string | null;
  sessions: QuestSession[];
  isCompleted: boolean;
}

interface QuestSession {
  shortDescription: string;
  thumbnail: string;
  sessionCompletionDate: string | null;
}

@Component({
  selector: 'app-quests',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule
  ],
  templateUrl: './quests.component.html',
  styleUrl: './quests.component.css'
})
export class QuestsComponent implements OnInit {
  @Input() memberId: any;

  questsData: Quest[] = [];
  filteredQuestsData: Quest[] = [];
  searchQuery: string = '';
  expandedQuestIndex: number | null = null;
  statusFilter: string = 'all';
  isLoading: boolean = false;

  constructor(private dashboard: MemberDashboardService) {}

  ngOnInit(): void {
    // Load quests data using only memberId
    this.getMemberQuests();
  }

  getMemberQuests() {
    this.isLoading = true;
    this.dashboard.getMemberQuestsByMemberId(this.memberId).subscribe({
      next: (response) => {
        this.questsData = response as any;
        this.questsData = (this.questsData as any).result || [];
        this.applyFilters();
        this.expandedQuestIndex = null; // Reset expanded state when new data loads
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching quests data:', error);
        this.questsData = [];
        this.filteredQuestsData = [];
        this.isLoading = false;
      }
    });
  }

  searchQuests() {
    this.applyFilters();
  }

  filterByStatus(status: string) {
    this.statusFilter = status;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.questsData];

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(quest =>
        quest.questName.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(quest => {
        const status = this.getQuestStatus(quest);
        if (this.statusFilter === 'in-progress') {
          return status === 'In-Progress';
        } else if (this.statusFilter === 'completed') {
          return status === 'Completed';
        }
        return true;
      });
    }

    // Sort by startDateTime ascending
    filtered.sort((a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime());
    this.filteredQuestsData = filtered;
    this.expandedQuestIndex = null; // Reset expanded state when filtering
  }

  toggleQuestExpansion(index: number) {
    const wasExpanded = this.expandedQuestIndex === index;
    this.expandedQuestIndex = wasExpanded ? null : index;

    // Scroll to quest card when expanding
    if (!wasExpanded) {
      setTimeout(() => {
        const questCard = document.querySelectorAll('.quest-card')[index] as HTMLElement;
        if (questCard) {
          questCard.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }

  isQuestExpanded(index: number): boolean {
    return this.expandedQuestIndex === index;
  }

  getQuestStatus(quest: Quest): string {
    if (quest.isCompleted || quest.completedDateTime) {
      return 'Completed';
    }
    return 'In-Progress';
  }

  getSessionStatus(session: QuestSession): string {
    if (session.sessionCompletionDate) {
      return 'Completed';
    }
    return 'Not Started';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  getMemberInitial(): string {
    // You can get this from member data if available
    return 'K';
  }

  hasQuests(): boolean {
    return this.filteredQuestsData && this.filteredQuestsData.length > 0;
  }
}